import SectionHeader from '../../components/admin/SectionHeader';
import Badge from '../../components/admin/Badge';
import React, { useState, useEffect, useRef, useCallback } from 'react';

const EMAIL_TEMPLATES = [
  { id: 'activation',           label: 'Service Ready',        icon: 'fa-rocket',           desc: 'Service has been provisioned and is ready to use', fields: [
    { key: 'accessUrl',     label: 'Access URL',      type: 'text',     optional: true },
    { key: 'accessDetails', label: 'Access Details',  type: 'textarea', optional: true },
  ]},
  { id: 'order_confirmation',   label: 'Order Confirmation',   icon: 'fa-receipt',          desc: 'Purchase confirmation and subscription details', fields: [] },
  { id: 'payment_failure',      label: 'Payment Failed',       icon: 'fa-circle-xmark',     desc: 'Payment failure notification with retry link', fields: [] },
  { id: 'service_suspended',    label: 'Service Suspended',    icon: 'fa-pause',            desc: 'Service has been suspended due to non-payment', fields: [] },
  { id: 'service_resumed',      label: 'Service Resumed',      icon: 'fa-play',             desc: 'Suspended service has been reactivated', fields: [
    { key: 'accessUrl', label: 'Access URL', type: 'text', optional: true },
  ]},
  { id: 'service_removed',      label: 'Service Cancelled',    icon: 'fa-trash',            desc: 'Subscription cancelled and service removed', fields: [] },
  { id: 'subscription_rejected',label: 'Request Rejected',     icon: 'fa-ban',              desc: 'Subscription or trial request was rejected', fields: [] },
  { id: 'plan_upgrade',         label: 'Plan Upgraded',        icon: 'fa-arrow-up',         desc: 'Plan upgrade approved and applied', fields: [
    { key: 'oldPlanName', label: 'Previous Plan', type: 'text', optional: true },
    { key: 'newPlanName', label: 'New Plan',      type: 'text', optional: true },
  ]},
  { id: 'plan_downgrade',       label: 'Plan Downgraded',      icon: 'fa-arrow-down',       desc: 'Plan downgrade approved and applied', fields: [
    { key: 'oldPlanName',   label: 'Previous Plan',        type: 'text', optional: true },
    { key: 'newPlanName',   label: 'New Plan',             type: 'text', optional: true },
    { key: 'creditAmount',  label: 'Credit Amount ($)',    type: 'text', optional: true },
  ]},
  { id: 'upgrade_pending',      label: 'Change Pending',       icon: 'fa-clock',            desc: 'Plan change request received and awaiting admin review', fields: [
    { key: 'oldPlanName', label: 'Current Plan',   type: 'text', optional: true },
    { key: 'newPlanName', label: 'Requested Plan', type: 'text', optional: true },
  ]},
  { id: 'price_update',         label: 'Price Updated',        icon: 'fa-tag',              desc: 'Subscription price has been updated', fields: [] },
  { id: 'incomplete_setup',     label: 'Setup Help',           icon: 'fa-circle-question',  desc: 'Remind user their service is ready and offer onboarding assistance', fields: [
    { key: 'accessUrl', label: 'Access URL', type: 'text', optional: true },
  ]},
  { id: 'resource_abuse_warning', label: 'Fair Usage Warning',   icon: 'fa-triangle-exclamation', desc: 'Warn customer they are exceeding fair usage limits — upgrade or reduce usage within deadline to avoid suspension', fields: [
    { key: 'exceededResources', label: 'Exceeded Resources', type: 'textarea', optional: false, placeholder: 'e.g. RAM: 3.8 GB used / 2 GB limit\nCPU: consistently above 90% for 48 hours\nStorage: 52 GB used / 20 GB limit' },
    { key: 'deadline',          label: 'Deadline',           type: 'text',     optional: true,  placeholder: '24 hours' },
  ]},
  { id: 'trial_extension_offer', label: 'Trial Extension Offer', icon: 'fa-star',             desc: 'Offer the customer extra free trial days in exchange for leaving a Trustpilot review', fields: [
    { key: 'extensionDays', label: 'Extension Days', type: 'text', optional: false, placeholder: '7' },
    { key: 'couponCode',    label: 'Coupon Code (optional)', type: 'text', optional: true, placeholder: 'e.g. REVIEW7' },
    { key: 'expiresDate',   label: 'Offer Expires (optional)', type: 'text', optional: true, placeholder: 'e.g. April 30, 2026' },
  ]},
  { id: 'service_update',       label: 'Service Updated',      icon: 'fa-rotate',           desc: 'Notify customer their service has been updated to a new version', fields: [
    { key: 'newVersion',    label: 'New Version',              type: 'text',     optional: false, placeholder: 'e.g. 2.14.2' },
    { key: 'releaseNotes',  label: 'Release Notes (optional)', type: 'textarea', optional: true,  placeholder: 'e.g. Bug fixes and performance improvements' },
  ]},
  { id: 'custom',               label: 'Custom Message',       icon: 'fa-pen',              desc: 'Write a one-off email to this customer. Sent with OpenHosst branding.', fields: [
    { key: 'subject',   label: 'Subject',         type: 'text',     optional: false, placeholder: 'e.g. A quick update about your instance' },
    { key: 'body',      label: 'Message',         type: 'textarea', optional: false, placeholder: 'Write your message…' },
    { key: 'cta_label', label: 'Button label',    type: 'text',     optional: true,  placeholder: 'e.g. Open my portal' },
    { key: 'cta_url',   label: 'Button URL',      type: 'text',     optional: true,  placeholder: 'https://…' },
  ]},
];

function AdminSubscriptions({ apiFetch, token, subscriptions, refreshSubscriptions, selectedSubs, setSelectedSubs, showOffers }) {
  const [expandedId, setExpandedId] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSrv, setFilterSrv] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterInterval, setFilterInterval] = useState('all');
  const [filterStripeStatus, setFilterStripeStatus] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterFraud, setFilterFraud] = useState(null); // { type: 'ip'|'fingerprint', value: string }
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [markingUndeployed, setMarkingUndeployed] = useState({});
  const [subAccessValues, setSubAccessValues] = useState({});
  const [servers, setServers] = useState([]);
  const [deployLog, setDeployLog] = useState({}); // { [subId]: { logId, log, polling } }
  const [latestDeployStatus, setLatestDeployStatus] = useState({}); // { [subId]: { action, status } }
  const pollTimers = useRef({});
  const [deploymentMode, setDeploymentMode] = useState('manual');

  // Fetch servers list on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/admin/servers');
        if (res.ok) {
          const data = await res.json();
          setServers(data.servers || data || []);
        }
      } catch {}
    })();
  }, [apiFetch]);

  // Fetch deployment mode on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/admin/settings/deployment-mode');
        if (res.ok) {
          const data = await res.json();
          setDeploymentMode(data.mode || 'manual');
        }
      } catch {}
    })();
  }, [apiFetch]);

  // Cleanup poll timers on unmount
  useEffect(() => {
    return () => {
      Object.values(pollTimers.current).forEach(t => clearInterval(t));
    };
  }, []);

  const statusColor = (s) => {
    if (s === 'active') return '#22c55e';
    if (s === 'pending') return '#f59e0b';
    if (s === 'suspended') return '#ef4444';
    if (s === 'rejected') return '#7c3aed';
    return '#6b7280';
  };

  const needsUndeployList = subscriptions.filter(s => s.needsUndeploy);
  // Only exclude undeployed — suspended still needs action, cancelled subs are tracked via local DB
  const pendingSuspension = subscriptions.filter(s => s.status === 'past_due' && s.hours_past_due >= 24 && s.service_status !== 'undeployed' && s.service_status !== 'suspended');
  // Active in Stripe (paid) but suspended in our system — needs access restored
  const needsUnsuspend = subscriptions.filter(s => s.status === 'active' && s.service_status === 'suspended');

  // Service status filter
  const filteredBySvcStatus = filterStatus === 'all'
    ? subscriptions.filter(s => s.status !== 'canceled' && !s.undeployed)
    : filterStatus === 'rejected'
      ? subscriptions.filter(s => s.service_status === 'rejected' || s.isTrialCancellation)
      : filterStatus === 'needs_undeploy'
        ? subscriptions.filter(s => s.needsUndeploy)
        : filterStatus === 'undeployed'
          ? subscriptions.filter(s => s.undeployed)
          : subscriptions.filter(s => (s.service_status || 'pending') === filterStatus && !s.undeployed);

  // Stripe status filter (applied on top of service status filter)
  // Note: 'canceled' filter bypasses filteredBySvcStatus which excludes cancelled subs
  const filtered = filterStripeStatus === 'all'
    ? filteredBySvcStatus
    : filterStripeStatus === 'scheduled_cancel'
      ? filteredBySvcStatus.filter(s => s.cancel_at_period_end && s.status !== 'canceled')
      : filterStripeStatus === 'canceled'
        ? subscriptions.filter(s => s.status === 'canceled')
        : filteredBySvcStatus.filter(s => s.status === filterStripeStatus);

  const filteredBySrv = filterSrv === 'all' ? filtered : filtered.filter(s => (s.srv || '') === filterSrv);

  const filteredByService = filterService === 'all' ? filteredBySrv : filteredBySrv.filter(s => (s.service || '') === filterService);
  const filteredByPlan = filterPlan === 'all' ? filteredByService : filteredByService.filter(s => (s.planSlug || '') === filterPlan);
  const filteredByInterval = filterInterval === 'all' ? filteredByPlan : filteredByPlan.filter(s => (s.billingInterval || 'month') === filterInterval);

  const filteredByFraud = filterFraud
    ? filteredByInterval.filter(s => {
        if (filterFraud.type === 'ip') return s.checkout_ip === filterFraud.value || s.signup_ip === filterFraud.value;
        if (filterFraud.type === 'fingerprint') return s.checkout_fingerprint === filterFraud.value || s.signup_fingerprint === filterFraud.value;
        return true;
      })
    : filteredByInterval;

  const searched = search
    ? filteredByFraud.filter(s =>
        (s.customerEmail || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.domain || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.planSlug || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.planName || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.id || '').toLowerCase().includes(search.toLowerCase())
      )
    : filteredByFraud;

  const totalPages = pageSize === 0 ? 1 : Math.ceil(searched.length / pageSize);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const paged = pageSize === 0 ? searched : searched.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Reset to page 1 on filter/search change
  const applySearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(1); };
  const clearSearch = () => { setSearch(''); setSearchInput(''); setPage(1); };
  const clearFraudFilter = () => { setFilterFraud(null); setPage(1); };
  const applyFraudFilter = (type, value) => { setFilterFraud({ type, value }); setPage(1); };
  const applyFilter = (key) => { setFilterStatus(key); setPage(1); };
  const applyStripeFilter = (key) => { setFilterStripeStatus(key); setPage(1); };
  const applySrvFilter = (srv) => { setFilterSrv(srv); setPage(1); };
  const applyServiceFilter = (svc) => { setFilterService(svc); setFilterPlan('all'); setPage(1); };
  const applyPlanFilter = (plan) => { setFilterPlan(plan); setPage(1); };
  const applyIntervalFilter = (iv) => { setFilterInterval(iv); setPage(1); };

  const toggleSub = (id) => setSelectedSubs(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const toggleAll = () => setSelectedSubs(prev => prev.size === paged.length ? new Set() : new Set(paged.map(s => s.id)));

  const startLogPolling = useCallback((subId, logId) => {
    // Clear any existing poll for this sub
    if (pollTimers.current[subId]) clearInterval(pollTimers.current[subId]);

    setDeployLog(prev => ({ ...prev, [subId]: { logId, log: null, polling: true } }));

    const poll = async () => {
      try {
        const res = await apiFetch(`/api/admin/deployment-log/${logId}`);
        if (res.ok) {
          const data = await res.json();
          const log = data.log;
          setDeployLog(prev => ({ ...prev, [subId]: { logId, log, polling: log.status === 'running' } }));
          setLatestDeployStatus(prev => ({ ...prev, [subId]: { action: log.action, status: log.status } }));
          if (log.status !== 'running') {
            clearInterval(pollTimers.current[subId]);
            delete pollTimers.current[subId];
            refreshSubscriptions();
          }
        }
      } catch {}
    };

    poll(); // immediate first poll
    pollTimers.current[subId] = setInterval(poll, 2000);
  }, [apiFetch, refreshSubscriptions]);

  const loadLatestDeployStatus = useCallback(async (subId) => {
    try {
      const res = await apiFetch(`/api/admin/deployment-logs/${subId}`);
      if (res.ok) {
        const data = await res.json();
        const logs = data.logs || [];
        if (logs.length > 0) {
          const latest = logs[0]; // most recent
          setLatestDeployStatus(prev => ({ ...prev, [subId]: { action: latest.action, status: latest.status, created_at: latest.created_at } }));
        }
      }
    } catch {}
  }, [apiFetch]);

  const handleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    const sub = subscriptions.find(s => s.id === id);
    if (sub) {
      setEditFields(prev => ({ ...prev, [id]: { service_status: sub.service_status || 'pending', srv: sub.srv || '' } }));
      if (sub.planSlug) loadSubAccessValues(id, sub.planSlug);
      loadLatestDeployStatus(id);
    }
  };

  // Called from alert notices — clears filters so the row is guaranteed visible, then scrolls to it
  const handleManage = (id) => {
    setFilterStatus('all');
    setFilterStripeStatus('all');
    setFilterSrv('all');
    setFilterService('all');
    setFilterPlan('all');
    setFilterInterval('all');
    setSearch('');
    setSearchInput('');
    setPage(1);
    setExpandedId(id);
    const sub = subscriptions.find(s => s.id === id);
    if (sub) {
      setEditFields(prev => ({ ...prev, [id]: { service_status: sub.service_status || 'pending', srv: sub.srv || '' } }));
      if (sub.planSlug) loadSubAccessValues(id, sub.planSlug);
      loadLatestDeployStatus(id);
    }
    // Scroll after React re-renders
    setTimeout(() => {
      const el = document.getElementById('sub-row-' + id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  };

  const handleFieldChange = (id, field, value) => setEditFields(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));

  const loadSubAccessValues = async (subId) => {
    try {
      const res = await apiFetch(`/api/admin/subscriptions/${subId}/access-values`);
      const data = await res.json();
      setSubAccessValues(prev => ({ ...prev, [subId]: data.fields || [] }));
    } catch { setSubAccessValues(prev => ({ ...prev, [subId]: [] })); }
  };

  const saveSubAccessValues = async (subId) => {
    try {
      const values = subAccessValues[subId] || [];
      await apiFetch(`/api/admin/subscriptions/${subId}/access-values`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ values }) });
    } catch (err) { console.error('Save access values error:', err); }
  };

  // Determine if the current status change requires a deployment action
  const getDeployAction = (sub, fields) => {
    const prevStatus = sub.service_status || 'pending';
    const newStatus = fields?.service_status;
    if (!newStatus || newStatus === prevStatus) return null;
    if (prevStatus === 'pending' && newStatus === 'active') return 'deploy';
    if (prevStatus === 'active' && newStatus === 'suspended') return 'suspend';
    if (prevStatus === 'suspended' && newStatus === 'active') return 'unsuspend';
    if (newStatus === 'undeployed' && sub.srv) return 'undeploy';
    return null;
  };

  const handleSave = async (id, skipEmail = false) => {
    const sub = subscriptions.find(s => s.id === id);
    const fields = editFields[id] || {};
    const action = sub ? getDeployAction(sub, fields) : null;

    // Confirmation dialogs for destructive actions
    if (action === 'suspend') {
      const msg = deploymentMode === 'ssh' ? 'This will stop the containers on the server via SSH. Continue?'
        : deploymentMode === 'n8n' ? 'This will send a suspend action to n8n. Continue?'
        : 'This will update the status to suspended. Continue?';
      if (!window.confirm(msg)) return;
    }
    if (action === 'undeploy') {
      const msg = deploymentMode === 'ssh' ? 'This will permanently remove the containers and data from the server via SSH. This cannot be undone. Continue?'
        : deploymentMode === 'n8n' ? 'This will send an undeploy action to n8n. Continue?'
        : 'This will mark the subscription as undeployed. Continue?';
      if (!window.confirm(msg)) return;
    }

    // Validate server selection for pending→active (SSH mode only)
    if (action === 'deploy' && deploymentMode === 'ssh' && !fields.server_id) {
      setSaveStatus(prev => ({ ...prev, [id]: 'Error: Please select a server before activating (SSH mode)' }));
      return;
    }

    setSaving(prev => ({ ...prev, [id]: true }));
    setSaveStatus(prev => ({ ...prev, [id]: '' }));
    try {
      const res = await apiFetch(`/api/admin/subscriptions/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...fields, skip_email: skipEmail }) });
      if (!res.ok) { const err = await res.json(); setSaveStatus(prev => ({ ...prev, [id]: `Error: ${err.error}` })); return; }
      const data = await res.json();

      // If backend returned a logId, start polling the deployment log
      if (data.logId) {
        startLogPolling(id, data.logId);
        const actionLabel = { deploy: 'Deployment', suspend: 'Suspend', unsuspend: 'Unsuspend', undeploy: 'Undeploy' }[action] || 'Action';
        setSaveStatus(prev => ({ ...prev, [id]: `${actionLabel} started...` }));
      } else if (data.success && action) {
        // manual/n8n mode: action completed immediately
        refreshSubscriptions();
        setSaveStatus(prev => ({ ...prev, [id]: data.message || 'Done!' }));
      } else {
        // Normal save (no deployment action)
        if (subAccessValues[id] && subAccessValues[id].length > 0) { try { await saveSubAccessValues(id); } catch {} }
        refreshSubscriptions();
        const statusMsg = fields.service_status === 'active'
          ? (skipEmail ? 'Saved — no email sent' : 'Saved & activation email sent!')
          : fields.service_status === 'rejected'
            ? (skipEmail ? 'Subscription cancelled — no email sent' : 'Subscription cancelled & rejection email sent!')
            : fields.service_status === 'suspended'
              ? (skipEmail ? 'Saved — no email sent' : 'Saved & suspension email sent!')
              : 'Saved!';
        setSaveStatus(prev => ({ ...prev, [id]: statusMsg }));
      }
    } catch { setSaveStatus(prev => ({ ...prev, [id]: 'Error: network failure' })); }
    finally { setSaving(prev => ({ ...prev, [id]: false })); }
  };

  const handleMarkUndeployed = async (id) => {
    setMarkingUndeployed(prev => ({ ...prev, [id]: true }));
    try {
      const res = await apiFetch(`/api/admin/subscriptions/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ service_status: 'undeployed' }) });
      if (!res.ok) { const err = await res.json(); alert(`Error: ${err.error}`); return; }
      const data = await res.json();
      if (data.logId) {
        startLogPolling(id, data.logId);
      } else {
        refreshSubscriptions();
      }
    } catch { alert('Network error'); }
    finally { setMarkingUndeployed(prev => ({ ...prev, [id]: false })); }
  };


  // Quick deploy/suspend/unsuspend/undeploy from the table row
  const [quickActing, setQuickActing] = useState({});
  const [emailModal, setEmailModal] = useState(null); // { sub }
  const [emailTemplate, setEmailTemplate] = useState('activation');
  const [emailOverrides, setEmailOverrides] = useState({});
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');
  const handleQuickAction = async (sub, action) => {
    const labels = { deploy: 'Activate', suspend: 'Suspend', unsuspend: 'Unsuspend', undeploy: 'Undeploy' };
    const confirmMsgs = {
      suspend: deploymentMode === 'ssh' ? 'Suspend this service (SSH)?' : 'Suspend this service?',
      undeploy: deploymentMode === 'ssh' ? 'Undeploy this service? Containers will be removed via SSH.' : 'Mark this service as undeployed?',
    };
    if (confirmMsgs[action] && !window.confirm(confirmMsgs[action])) return;

    setQuickActing(prev => ({ ...prev, [sub.id]: action }));
    try {
      if (action === 'deploy') {
        // For SSH mode, need to pick a server — expand the row instead
        if (deploymentMode === 'ssh') {
          handleExpand(sub.id);
          setEditFields(prev => ({ ...prev, [sub.id]: { service_status: 'active', srv: sub.srv || '' } }));
          setQuickActing(prev => ({ ...prev, [sub.id]: null }));
          return;
        }
        // manual/n8n: use PATCH to set active
        const res = await apiFetch(`/api/admin/subscriptions/${sub.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ service_status: 'active' })
        });
        const data = await res.json();
        if (data.logId) startLogPolling(sub.id, data.logId);
        else refreshSubscriptions();
      } else if (action === 'undeploy') {
        const res = await apiFetch(`/api/admin/subscriptions/${sub.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ service_status: 'undeployed' })
        });
        const data = await res.json();
        if (data.logId) startLogPolling(sub.id, data.logId);
        else refreshSubscriptions();
      } else if (action === 'suspend') {
        const res = await apiFetch(`/api/admin/subscriptions/${sub.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ service_status: 'suspended' })
        });
        const data = await res.json();
        if (data.logId) startLogPolling(sub.id, data.logId);
        else refreshSubscriptions();
      } else if (action === 'unsuspend') {
        const res = await apiFetch(`/api/admin/subscriptions/${sub.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ service_status: 'active' })
        });
        const data = await res.json();
        if (data.logId) startLogPolling(sub.id, data.logId);
        else refreshSubscriptions();
      }
    } catch (err) { console.error('Quick action error:', err); }
    finally { setQuickActing(prev => ({ ...prev, [sub.id]: null })); }
  };

  const handleApproveUpgrade = async (sub) => {
    const pendingLabel = sub.upgradeType === 'billing_change' ? 'this billing cycle change' : `upgrade to ${sub.pendingUpgradeToPlan}`;
    if (!window.confirm(`Approve ${pendingLabel}?`)) return;
    setSaving(prev => ({ ...prev, [sub.id]: true })); setSaveStatus(prev => ({ ...prev, [sub.id]: '' }));
    try {
      const res = await apiFetch(`/api/admin/subscriptions/${sub.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ service_status: 'approve_upgrade' }) });
      if (!res.ok) { const err = await res.json(); setSaveStatus(prev => ({ ...prev, [sub.id]: `Error: ${err.error}` })); return; }
      setSaveStatus(prev => ({ ...prev, [sub.id]: 'Upgrade approved & email sent!' })); refreshSubscriptions();
    } catch { setSaveStatus(prev => ({ ...prev, [sub.id]: 'Error: network failure' })); }
    finally { setSaving(prev => ({ ...prev, [sub.id]: false })); }
  };

  const handleRejectUpgrade = async (sub) => {
    const newSubId = sub.pendingUpgradeTo;
    if (!newSubId) { alert('Could not find new subscription ID to reject.'); return; }
    if (!window.confirm('Reject this upgrade? The new subscription will be cancelled and the charge refunded.')) return;
    setSaving(prev => ({ ...prev, [sub.id]: true })); setSaveStatus(prev => ({ ...prev, [sub.id]: '' }));
    try {
      const res = await apiFetch(`/api/admin/subscriptions/${newSubId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ service_status: 'reject_upgrade' }) });
      if (!res.ok) { const err = await res.json(); setSaveStatus(prev => ({ ...prev, [sub.id]: `Error: ${err.error}` })); return; }
      setSaveStatus(prev => ({ ...prev, [sub.id]: 'Upgrade rejected & refund issued!' })); refreshSubscriptions();
    } catch { setSaveStatus(prev => ({ ...prev, [sub.id]: 'Error: network failure' })); }
    finally { setSaving(prev => ({ ...prev, [sub.id]: false })); }
  };

  const handleCancelUpgradeAdmin = async (sub) => {
    if (!window.confirm('Cancel this upgrade immediately? The new subscription will be removed with no refund. The customer keeps their current plan.')) return;
    setSaving(prev => ({ ...prev, [sub.id]: true })); setSaveStatus(prev => ({ ...prev, [sub.id]: '' }));
    try {
      const res = await apiFetch(`/api/admin/subscriptions/${sub.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ service_status: 'cancel_upgrade' }) });
      if (!res.ok) { const err = await res.json(); setSaveStatus(prev => ({ ...prev, [sub.id]: `Error: ${err.error}` })); return; }
      setSaveStatus(prev => ({ ...prev, [sub.id]: 'Upgrade cancelled.' })); refreshSubscriptions();
    } catch { setSaveStatus(prev => ({ ...prev, [sub.id]: 'Error: network failure' })); }
    finally { setSaving(prev => ({ ...prev, [sub.id]: false })); }
  };

  const handleApproveLegacyDowngrade = async (sub) => {
    if (!window.confirm(`Approve downgrade for ${sub.customerEmail}? This will activate their new plan and send a confirmation email.`)) return;
    setSaving(prev => ({ ...prev, [sub.id]: true })); setSaveStatus(prev => ({ ...prev, [sub.id]: '' }));
    try {
      const res = await apiFetch(`/api/admin/subscriptions/${sub.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ service_status: 'approve_legacy_downgrade' }) });
      if (!res.ok) { const err = await res.json(); setSaveStatus(prev => ({ ...prev, [sub.id]: `Error: ${err.error}` })); return; }
      setSaveStatus(prev => ({ ...prev, [sub.id]: 'Downgrade approved & email sent!' })); refreshSubscriptions();
    } catch { setSaveStatus(prev => ({ ...prev, [sub.id]: 'Error: network failure' })); }
    finally { setSaving(prev => ({ ...prev, [sub.id]: false })); }
  };

  const openEmailModal = (sub) => {
    setEmailTemplate('activation');
    setEmailOverrides({
      oldPlanName: sub.planName || '',
      newPlanName: sub.planName || '',
    });
    setEmailStatus('');
    setEmailModal({ sub });
  };

  const handleSendEmail = async () => {
    // Custom message uses the ad-hoc endpoint (free subject/body); templates use the templated one.
    if (emailTemplate === 'custom') {
      if (!emailOverrides.subject?.trim() || !emailOverrides.body?.trim()) {
        setEmailStatus('error:Subject and message are required'); return;
      }
    }
    setEmailSending(true); setEmailStatus('');
    try {
      let res;
      if (emailTemplate === 'custom') {
        res = await apiFetch(`/api/admin/subscriptions/${emailModal.sub.id}/email`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject: emailOverrides.subject, body: emailOverrides.body, cta_label: emailOverrides.cta_label, cta_url: emailOverrides.cta_url }),
        });
      } else {
        res = await apiFetch('/api/admin/send-subscription-email', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscriptionId: emailModal.sub.id, templateId: emailTemplate, overrides: emailOverrides })
        });
      }
      if (!res.ok) { const err = await res.json(); setEmailStatus('error:' + err.error); return; }
      const data = await res.json();
      setEmailStatus('sent:' + (data.sentTo || data.to || emailModal.sub.customerEmail));
    } catch { setEmailStatus('error:Network failure'); }
    finally { setEmailSending(false); }
  };

  const handleCancelLegacyDowngrade = async (sub) => {
    if (!window.confirm('Cancel this downgrade? The pending subscription will be removed. The customer keeps their current plan.')) return;
    setSaving(prev => ({ ...prev, [sub.id]: true })); setSaveStatus(prev => ({ ...prev, [sub.id]: '' }));
    try {
      const res = await apiFetch(`/api/admin/subscriptions/${sub.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ service_status: 'cancel_legacy_downgrade' }) });
      if (!res.ok) { const err = await res.json(); setSaveStatus(prev => ({ ...prev, [sub.id]: `Error: ${err.error}` })); return; }
      setSaveStatus(prev => ({ ...prev, [sub.id]: 'Downgrade cancelled.' })); refreshSubscriptions();
    } catch { setSaveStatus(prev => ({ ...prev, [sub.id]: 'Error: network failure' })); }
    finally { setSaving(prev => ({ ...prev, [sub.id]: false })); }
  };

  // Helper to check if pending→active transition needs server selection
  const needsServerSelect = (sub, fields) => {
    const prevStatus = sub.service_status || 'pending';
    const newStatus = fields?.service_status;
    return prevStatus === 'pending' && newStatus === 'active' && deploymentMode === 'ssh';
  };

  // Helper to check if save should be disabled
  const isSaveDisabled = (sub, fields) => {
    if (saving[sub.id]) return true;
    if (needsServerSelect(sub, fields) && !fields?.server_id) return true;
    return false;
  };

  // Check if pending→active (any mode)
  const isPendingToActive = (sub, fields) => {
    const prevStatus = sub.service_status || 'pending';
    return prevStatus === 'pending' && fields?.service_status === 'active';
  };

  const deployStatusBadge = (subId) => {
    const ds = latestDeployStatus[subId];
    if (!ds) return null;
    const colors = { success: '#22c55e', failed: '#ef4444', running: '#3b82f6' };
    const icons = { deploy: 'fa-rocket', suspend: 'fa-pause', unsuspend: 'fa-play', undeploy: 'fa-trash' };
    return (
      <span style={{ fontSize: '11px', color: colors[ds.status] || '#6b7280', marginLeft: '8px' }}>
        <i className={`fa-solid ${icons[ds.action] || 'fa-circle'}`} style={{ marginRight: 3 }}></i>
        {ds.action} {ds.status}
      </span>
    );
  };

  return (
    <div>
      <div className="admin-section-header">
        <h2>Subscriptions</h2>
        <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={refreshSubscriptions}>Refresh</button>
      </div>

      {/* Needs Undeployment Alert */}
      {needsUndeployList.length > 0 && (
        <div className="admin-alert admin-alert--info" style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: '700' }}>Needs Undeployment — {needsUndeployList.length} cancelled subscription{needsUndeployList.length > 1 ? 's' : ''} still deployed</p>
          <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: 'var(--admin-text-secondary)' }}>These subscriptions are cancelled but their server is still assigned. Click "Undeploy" to remove containers via SSH.</p>
          <table className="admin-table">
            <thead><tr>{['Email', 'Plan', 'Domain', 'Srv', 'Status', 'Date', 'Action'].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {needsUndeployList.map(sub => (
                <tr key={sub.id}>
                  <td>{sub.customerEmail}</td>
                  <td>{sub.planName || sub.planSlug}</td>
                  <td style={{ color: 'var(--admin-text-secondary)' }}>{sub.domain || '—'}</td>
                  <td><span className="admin-badge admin-badge--info">srv {sub.srv}</span></td>
                  <td style={{ color: 'var(--admin-text-secondary)' }}>{sub.status}</td>
                  <td style={{ color: 'var(--admin-text-secondary)' }}>{sub.cancel_at_period_end ? `Cancels ${new Date(sub.current_period_end * 1000).toLocaleDateString()}` : sub.canceled_at ? `Cancelled ${new Date(sub.canceled_at * 1000).toLocaleDateString()}` : '—'}</td>
                  <td><button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => handleMarkUndeployed(sub.id)} disabled={markingUndeployed[sub.id]}>{markingUndeployed[sub.id] ? 'Undeploying...' : 'Undeploy'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending Suspension Alert */}
      {pendingSuspension.length > 0 && (
        <div className="admin-alert admin-alert--error" style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: '700' }}>Pending Suspension — {pendingSuspension.length} subscription{pendingSuspension.length > 1 ? 's' : ''} overdue &gt;24 hours</p>
          <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: 'var(--admin-text-secondary)' }}>Set service status to "suspended" to stop their containers and notify the customer.</p>
          <table className="admin-table">
            <thead><tr>{['Email', 'Plan', 'Domain', 'Hours Overdue', 'Service Status', 'Action'].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {pendingSuspension.map(sub => (
                <tr key={sub.id}>
                  <td>{sub.customerEmail}</td>
                  <td>{sub.planName || sub.planSlug}</td>
                  <td style={{ color: 'var(--admin-text-secondary)' }}>{sub.domain || '—'}</td>
                  <td><span style={{ color: '#ef4444', fontWeight: '700' }}>{sub.hours_past_due > 0 ? sub.hours_past_due + 'h' : '<1h'}</span></td>
                  <td><span className={`admin-badge admin-badge--${sub.service_status === 'active' ? 'active' : 'pending'}`}>{sub.service_status || 'pending'}</span></td>
                  <td><button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => handleManage(sub.id)}>Manage</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Needs Unsuspend Alert */}
      {needsUnsuspend.length > 0 && (
        <div className="admin-alert admin-alert--warning" style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: '700' }}>
            <i className="fa-solid fa-circle-check" style={{ marginRight: '6px', color: '#22c55e' }}></i>
            Payment Restored — {needsUnsuspend.length} subscription{needsUnsuspend.length > 1 ? 's' : ''} active in Stripe but still suspended
          </p>
          <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: 'var(--admin-text-secondary)' }}>
            These customers have paid and their Stripe subscription is active. Restore access to unsuspend their service.
          </p>
          <table className="admin-table">
            <thead>
              <tr>{['Email', 'Plan', 'Domain', 'Srv', 'Stripe Status', 'Actions'].map(h => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {needsUnsuspend.map(sub => (
                <tr key={sub.id}>
                  <td>{sub.customerEmail}</td>
                  <td>{sub.planName || sub.planSlug}</td>
                  <td style={{ color: 'var(--admin-text-secondary)' }}>{sub.domain || '—'}</td>
                  <td>{sub.srv ? <span className="admin-badge admin-badge--info">{sub.srv}</span> : <span style={{ color: 'var(--admin-text-muted)' }}>—</span>}</td>
                  <td><span className="admin-badge admin-badge--active">active</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        className="admin-btn admin-btn--sm admin-btn--primary"
                        onClick={() => handleQuickAction(sub, 'unsuspend')}
                        disabled={!!quickActing[sub.id]}
                      >
                        {quickActing[sub.id] === 'unsuspend' ? 'Restoring…' : 'Restore Access'}
                      </button>
                      <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => handleManage(sub.id)}>
                        Manage
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Search bar */}
      <div className="admin-card" style={{ marginBottom: '12px', padding: '10px 14px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <form onSubmit={applySearch} style={{ display: 'flex', gap: '6px', flex: 1, minWidth: '200px' }}>
          <input
            className="admin-input"
            placeholder="Search email, domain, plan, subscription ID…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ flex: 1, fontSize: '13px' }}
          />
          <button type="submit" className="admin-btn admin-btn--sm admin-btn--primary"><i className="fa-solid fa-search" /></button>
          {search && <button type="button" className="admin-btn admin-btn--sm admin-btn--ghost" onClick={clearSearch}><i className="fa-solid fa-times" /></button>}
        </form>
        {filterFraud && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', flexShrink: 0 }}>
            <i className="fa-solid fa-shield-halved" style={{ color: '#f59e0b' }} />
            <span style={{ color: '#f59e0b', fontWeight: '600' }}>{filterFraud.type === 'ip' ? 'IP' : 'Device'}:</span>
            <code style={{ color: 'var(--admin-text-primary)', fontSize: '11px' }}>{filterFraud.type === 'fingerprint' ? filterFraud.value.slice(0, 12) + '…' : filterFraud.value}</code>
            <span style={{ color: 'var(--admin-text-muted)', fontSize: '11px' }}>({searched.length} match{searched.length !== 1 ? 'es' : ''})</span>
            <button onClick={clearFraudFilter} style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', padding: '0 2px', fontSize: '13px' }} title="Clear filter"><i className="fa-solid fa-times" /></button>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--admin-text-muted)', flexShrink: 0 }}>
          <span>Rows:</span>
          {[25, 50, 100, 200, 0].map(n => (
            <button
              key={n}
              className={`admin-btn admin-btn--sm ${pageSize === n ? 'admin-btn--primary' : 'admin-btn--ghost'}`}
              onClick={() => { setPageSize(n); setPage(1); }}
            >{n === 0 ? 'All' : n}</button>
          ))}
        </div>
      </div>

      {/* Filter bar */}
      {(() => {
        const srvOptions     = Array.from(new Set(subscriptions.map(s => s.srv).filter(Boolean))).sort();
        const serviceOptions = Array.from(new Set(subscriptions.map(s => s.service).filter(Boolean))).sort();
        const planBase       = filterService === 'all' ? subscriptions : subscriptions.filter(s => s.service === filterService);
        const planOptions    = Array.from(new Set(planBase.map(s => s.planSlug).filter(Boolean))).sort();
        const monthCount     = subscriptions.filter(s => (s.billingInterval || 'month') === 'month').length;
        const yearCount      = subscriptions.filter(s => s.billingInterval === 'year').length;
        const scheduledCount = subscriptions.filter(s => s.cancel_at_period_end && s.status !== 'canceled').length;
        const hasActiveFilter = filterStatus !== 'all' || filterStripeStatus !== 'all' || filterSrv !== 'all' || filterService !== 'all' || filterPlan !== 'all' || filterInterval !== 'all';
        const clearAll = () => { setFilterStatus('all'); setFilterStripeStatus('all'); setFilterSrv('all'); setFilterService('all'); setFilterPlan('all'); setFilterInterval('all'); setPage(1); };
        const selStyle = { fontSize: '12px', padding: '5px 8px', flexShrink: 0 };
        const selCls = 'admin-select admin-select--auto';
        return (
          <div className="admin-card" style={{ marginBottom: '12px', padding: '10px 14px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', overflowX: 'auto', minWidth: 0 }}>
              <i className="fa-solid fa-filter" style={{ fontSize: '12px', color: 'var(--admin-text-muted)', flexShrink: 0 }} />

              {/* Service status */}
              <select className={selCls} style={selStyle}
                value={filterStatus} onChange={e => applyFilter(e.target.value)}>
                <option value="all">Service: All</option>
                <option value="pending">Pending ({subscriptions.filter(s => (s.service_status || 'pending') === 'pending' && s.status !== 'canceled' && !s.undeployed).length})</option>
                <option value="active">Active ({subscriptions.filter(s => s.service_status === 'active' && s.status !== 'canceled' && !s.undeployed).length})</option>
                <option value="suspended">Suspended ({subscriptions.filter(s => s.service_status === 'suspended' && s.status !== 'canceled' && !s.undeployed).length})</option>
                <option value="rejected">Rejected ({subscriptions.filter(s => s.service_status === 'rejected' || s.isTrialCancellation).length})</option>
                <option value="undeployed">Undeployed ({subscriptions.filter(s => s.undeployed).length})</option>
                <option value="needs_undeploy">Needs Undeploy ({needsUndeployList.length})</option>
              </select>

              {/* Stripe status */}
              <select className={selCls} style={selStyle}
                value={filterStripeStatus} onChange={e => applyStripeFilter(e.target.value)}>
                <option value="all">Stripe: All</option>
                <option value="active">Active ({subscriptions.filter(s => s.status === 'active').length})</option>
                <option value="trialing">Trialing ({subscriptions.filter(s => s.status === 'trialing').length})</option>
                <option value="past_due">Past Due ({subscriptions.filter(s => s.status === 'past_due').length})</option>
                <option value="scheduled_cancel">Sched. Cancel ({scheduledCount})</option>
                <option value="canceled">Cancelled ({subscriptions.filter(s => s.status === 'canceled').length})</option>
                <option value="incomplete">Incomplete ({subscriptions.filter(s => s.status === 'incomplete').length})</option>
              </select>

              {serviceOptions.length >= 2 && (
                <select className={selCls} style={selStyle}
                  value={filterService} onChange={e => applyServiceFilter(e.target.value)}>
                  <option value="all">All services</option>
                  {serviceOptions.map(svc => (
                    <option key={svc} value={svc}>{svc} ({subscriptions.filter(s => s.service === svc).length})</option>
                  ))}
                </select>
              )}

              {planOptions.length >= 2 && (
                <select className={selCls} style={selStyle}
                  value={filterPlan} onChange={e => applyPlanFilter(e.target.value)}>
                  <option value="all">All plans</option>
                  {planOptions.map(slug => {
                    const label = planBase.find(s => s.planSlug === slug)?.planName || slug;
                    return <option key={slug} value={slug}>{label} ({planBase.filter(s => s.planSlug === slug).length})</option>;
                  })}
                </select>
              )}

              {monthCount > 0 && yearCount > 0 && (
                <select className={selCls} style={selStyle}
                  value={filterInterval} onChange={e => applyIntervalFilter(e.target.value)}>
                  <option value="all">All billing</option>
                  <option value="month">Monthly ({monthCount})</option>
                  <option value="year">Yearly ({yearCount})</option>
                </select>
              )}

              {srvOptions.length >= 2 && (
                <select className={selCls} style={selStyle}
                  value={filterSrv} onChange={e => applySrvFilter(e.target.value)}>
                  <option value="all">All servers</option>
                  {srvOptions.map(srv => (
                    <option key={srv} value={srv}>{srv} ({subscriptions.filter(s => s.srv === srv).length})</option>
                  ))}
                </select>
              )}

              {hasActiveFilter && (
                <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={clearAll} style={{ marginLeft: 'auto', fontSize: '12px', flexShrink: 0 }}>
                  <i className="fa-solid fa-times" style={{ marginRight: '4px' }} />Clear
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Main table */}
      <div className="admin-card" style={{ padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                {showOffers && <th style={{ width: '36px' }}><input type="checkbox" checked={filtered.length > 0 && selectedSubs.size === filtered.length} onChange={toggleAll} title="Select all" /></th>}
                {['Email', 'Plan', 'Interval', 'Domain', 'Srv', 'Service Status', 'Stripe Status', 'Overdue', 'Created', 'Source', 'Actions'].map(h => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {paged.map(sub => (
                <React.Fragment key={sub.id}>
                  <tr id={`sub-row-${sub.id}`} style={{ cursor: 'pointer', background: sub.needsUndeploy ? 'rgba(26,173,159,0.03)' : 'transparent' }} onClick={() => handleExpand(sub.id)}>
                    {showOffers && <td onClick={e => e.stopPropagation()} style={{ width: '36px' }}><input type="checkbox" checked={selectedSubs.has(sub.id)} onChange={() => toggleSub(sub.id)} /></td>}
                    <td>{sub.customerEmail}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {sub.planName || sub.planSlug}
                      {sub.pendingUpgradeToPlan && <span className="admin-badge admin-badge--pending" style={{ marginLeft: '6px', fontSize: '10px' }}>
                          <i className="fa-solid fa-clock-rotate-left" style={{marginRight:3}}></i>
                          {sub.upgradeType === 'billing_change'
                            ? (sub.billingInterval === 'year' ? '→ Monthly' : '→ Yearly')
                            : sub.upgradeType === 'plan_downgrade'
                              ? `↓ ${sub.pendingUpgradeToPlan}`
                              : `↑ ${sub.pendingUpgradeToPlan}`}
                        </span>}
                      {sub.isUpgradePending && <span className="admin-badge admin-badge--info" style={{ marginLeft: '6px', fontSize: '10px' }}>Pending Activation</span>}
                    </td>
                    <td style={{ color: 'var(--admin-text-muted)', fontSize: '12px' }}>{sub.isLifetime ? 'Lifetime' : (sub.billingInterval === 'year' ? 'Yearly' : 'Monthly')}</td>
                    <td style={{ color: 'var(--admin-text-secondary)', fontSize: '12px' }}>{sub.domain || '—'}</td>
                    <td>{sub.srv ? <span style={{ color: sub.needsUndeploy ? 'var(--admin-accent)' : 'var(--admin-text-secondary)', fontWeight: sub.needsUndeploy ? '700' : 'normal' }}>{sub.srv}</span> : '—'}</td>
                    <td>
                      <span className={`admin-badge admin-badge--${sub.service_status === 'active' ? 'active' : sub.service_status === 'suspended' ? 'suspended' : sub.service_status === 'rejected' ? 'rejected' : 'pending'}`}>{sub.service_status || 'pending'}</span>
                      {sub.isTrialCancellation && <span className="admin-badge admin-badge--rejected" style={{ marginLeft: '4px', fontSize: '10px' }}>trial cancel</span>}
                      {sub.needsUndeploy && <span className="admin-badge admin-badge--info" style={{ marginLeft: '4px', fontSize: '10px' }}>undeploy</span>}
                      {sub.isMigrationPaused && <span className="admin-badge admin-badge--pending" style={{ marginLeft: '4px', fontSize: '10px' }} title={`Paused until ${new Date(sub.migrationPausedUntil * 1000).toLocaleDateString()}`}>paused</span>}
                    </td>
                    <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                      <span style={{ color: 'var(--admin-text-secondary)' }}>{sub.status}</span>
                      {sub.cancel_at_period_end && sub.status !== 'canceled' && (
                        <span className="admin-badge admin-badge--warning" style={{ marginLeft: '4px', fontSize: '10px' }} title={`Cancels ${new Date(sub.current_period_end * 1000).toLocaleDateString()}`}>
                          <i className="fa-solid fa-clock" style={{ marginRight: '2px' }} />cancels {new Date(sub.current_period_end * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      {sub.status === 'past_due' ? (sub.hours_past_due > 0 ? <span style={{ color: sub.hours_past_due >= 24 ? '#ef4444' : '#f59e0b', fontWeight: '600' }}>{sub.hours_past_due}h</span> : <span style={{ color: '#f59e0b', fontWeight: '600' }}>&lt;1h</span>) : <span style={{ color: 'var(--admin-text-muted)' }}>—</span>}
                    </td>
                    <td style={{ color: 'var(--admin-text-secondary)', fontSize: '12px', whiteSpace: 'nowrap' }}>{new Date(sub.created * 1000).toLocaleDateString()}</td>
                    <td style={{ fontSize: '12px' }}>
                      {sub.utm_source
                        ? <span title={[sub.utm_source, sub.utm_medium, sub.utm_campaign, sub.utm_content].filter(Boolean).join(' / ')}>
                            <span className="admin-badge admin-badge--info">{sub.utm_source}</span>
                            {sub.utm_content ? <span style={{ color: 'var(--admin-text-muted)', marginLeft: '4px' }}>{sub.utm_content}</span> : null}
                          </span>
                        : <span style={{ color: 'var(--admin-text-muted)' }}>—</span>}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'nowrap' }}>
                        <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => handleExpand(sub.id)}>{expandedId === sub.id ? 'Close' : 'Edit'}</button>
                        <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={e => { e.stopPropagation(); openEmailModal(sub); }} title="Send email to customer"><i className="fa-solid fa-envelope" /></button>
                        {sub.service_status === 'pending' && sub.status !== 'canceled' && (
                          <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => handleQuickAction(sub, 'deploy')} disabled={!!quickActing[sub.id]} title={deploymentMode === 'ssh' ? 'Deploy (opens editor for server selection)' : deploymentMode === 'n8n' ? 'Send to n8n' : 'Activate'}>
                            {quickActing[sub.id] === 'deploy' ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-rocket" />}
                          </button>
                        )}
                        {sub.service_status === 'active' && (
                          <>
                            <button className="admin-btn admin-btn--sm admin-btn--warning" onClick={() => handleQuickAction(sub, 'suspend')} disabled={!!quickActing[sub.id]} title="Suspend" style={{ background: '#f59e0b', color: '#fff', border: 'none' }}>
                              {quickActing[sub.id] === 'suspend' ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-pause" />}
                            </button>
                            <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => handleQuickAction(sub, 'undeploy')} disabled={!!quickActing[sub.id]} title="Undeploy">
                              {quickActing[sub.id] === 'undeploy' ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-trash" />}
                            </button>
                          </>
                        )}
                        {sub.service_status === 'suspended' && (
                          <>
                            <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => handleQuickAction(sub, 'unsuspend')} disabled={!!quickActing[sub.id]} title="Unsuspend">
                              {quickActing[sub.id] === 'unsuspend' ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-play" />}
                            </button>
                            <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => handleQuickAction(sub, 'undeploy')} disabled={!!quickActing[sub.id]} title="Undeploy">
                              {quickActing[sub.id] === 'undeploy' ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-trash" />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {expandedId === sub.id && (
                    <tr className="admin-expanded-row">
                      <td colSpan={showOffers ? 12 : 11}>
                        {/* Upgrade pending panels */}
                        {sub.pendingUpgradeToPlan && (() => {
                          const isBillingSwitch = sub.upgradeType === 'billing_change';
                          const fmtPrice = (p, interval, cur) => p != null ? `${(cur||'USD')==='USD'?'$':''}${parseFloat(p).toFixed(2)}/${interval==='year'?'yr':'mo'}` : '—';
                          const curIntervalLabel = sub.billingInterval === 'year' ? 'Yearly' : 'Monthly';
                          const pd = sub.pendingUpgradeDetails;
                          const newIntervalLabel = pd ? (pd.interval === 'year' ? 'Yearly' : 'Monthly') : (sub.billingInterval === 'year' ? 'Monthly' : 'Yearly');
                          return (
                            <div className="admin-alert admin-alert--pending" style={{ marginBottom: '16px' }}>
                              {/* Header */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <i className={`fa-solid ${isBillingSwitch ? 'fa-arrows-rotate' : sub.upgradeType === 'plan_downgrade' ? 'fa-arrow-down' : 'fa-arrow-up'}`} style={{ color: '#f59e0b' }}></i>
                                <span style={{ fontWeight: '700', fontSize: '14px' }}>
                                  {isBillingSwitch ? 'Billing Cycle Change Request' : sub.upgradeType === 'plan_downgrade' ? 'Plan Downgrade Request' : 'Plan Upgrade Request'}
                                </span>
                                <span style={{ marginLeft: 'auto', fontSize: '11px', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>AWAITING APPROVAL</span>
                              </div>
                              {/* Detail grid */}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px', fontSize: '13px' }}>
                                <div style={{ background: 'var(--admin-bg)', borderRadius: '6px', padding: '10px 12px', border: '1px solid var(--admin-border)' }}>
                                  <div style={{ color: 'var(--admin-text-muted)', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Current</div>
                                  <div style={{ fontWeight: '600', color: 'var(--admin-text-primary)' }}>{sub.planName}</div>
                                  <div style={{ color: 'var(--admin-text-secondary)', marginTop: '2px' }}>{curIntervalLabel} · {fmtPrice(sub.price, sub.billingInterval, sub.currency)}</div>
                                </div>
                                <div style={{ background: 'var(--admin-bg)', borderRadius: '6px', padding: '10px 12px', border: '1px solid rgba(245,158,11,0.3)' }}>
                                  <div style={{ color: '#f59e0b', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Requested</div>
                                  <div style={{ fontWeight: '600', color: 'var(--admin-text-primary)' }}>{isBillingSwitch ? sub.planName : (sub.pendingUpgradeToPlan || '—')}</div>
                                  <div style={{ color: 'var(--admin-text-secondary)', marginTop: '2px' }}>{newIntervalLabel} · {pd ? fmtPrice(pd.price, pd.interval, pd.currency) : '—'}</div>
                                </div>
                              </div>
                              {/* Domain + credit */}
                              <div style={{ fontSize: '12px', color: 'var(--admin-text-secondary)', marginBottom: sub.proratedCredit > 0 ? '6px' : '12px' }}>
                                <i className="fa-solid fa-globe" style={{ marginRight: 4 }}></i>{sub.domain || 'Domain not set'}
                              </div>
                              {sub.proratedCredit > 0 && (
                                <div style={{ fontSize: '12px', color: 'var(--admin-success)', marginBottom: '12px' }}>
                                  <i className="fa-solid fa-circle-check" style={{ marginRight: 4 }}></i>
                                  Proration credit applied: <strong>${(sub.proratedCredit / 100).toFixed(2)}</strong>
                                </div>
                              )}
                              {/* Action buttons */}
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button className="admin-btn admin-btn--primary" onClick={() => handleApproveUpgrade(sub)} disabled={saving[sub.id]}>
                                  <i className="fa-solid fa-check"></i> {isBillingSwitch ? 'Approve Billing Change' : sub.upgradeType === 'plan_downgrade' ? 'Approve Downgrade' : 'Approve Upgrade'}
                                </button>
                                <button className="admin-btn admin-btn--danger" onClick={() => handleRejectUpgrade(sub)} disabled={saving[sub.id]}>
                                  <i className="fa-solid fa-rotate-left"></i> Reject & Refund
                                </button>
                                <button className="admin-btn admin-btn--ghost" onClick={() => handleCancelUpgradeAdmin(sub)} disabled={saving[sub.id]} style={{ border: '1px solid var(--admin-border)' }}>
                                  <i className="fa-solid fa-ban"></i> Cancel (No Refund)
                                </button>
                              </div>
                              {saveStatus[sub.id] && <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: saveStatus[sub.id].includes('Error') ? '#f87171' : 'var(--admin-success)' }}>{saveStatus[sub.id]}</p>}
                            </div>
                          );
                        })()}
                        {sub.isLegacyDowngrade && (() => {
                          const fmtPrice = (p, interval, cur) => p != null ? `${(cur||'USD')==='USD'?'$':''}${parseFloat(p).toFixed(2)}/${interval==='year'?'yr':'mo'}` : '—';
                          const intervalLabel = sub.billingInterval === 'year' ? 'Yearly' : 'Monthly';
                          return (
                            <div className="admin-alert admin-alert--pending" style={{ marginBottom: '16px' }}>
                              {/* Header */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <i className="fa-solid fa-arrow-down" style={{ color: '#f59e0b' }}></i>
                                <span style={{ fontWeight: '700', fontSize: '14px' }}>Plan Downgrade Request</span>
                                <span style={{ marginLeft: 'auto', fontSize: '11px', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>AWAITING APPROVAL</span>
                              </div>
                              {/* Detail grid — single box since previous sub was already cancelled by old flow */}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px', fontSize: '13px' }}>
                                <div style={{ background: 'var(--admin-bg)', borderRadius: '6px', padding: '10px 12px', border: '1px solid var(--admin-border)' }}>
                                  <div style={{ color: 'var(--admin-text-muted)', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Current</div>
                                  <div style={{ fontWeight: '600', color: 'var(--admin-text-muted)', fontStyle: 'italic' }}>Previous plan cancelled</div>
                                  <div style={{ color: 'var(--admin-text-muted)', marginTop: '2px', fontSize: '12px' }}>Original sub was cancelled when downgrade was requested</div>
                                </div>
                                <div style={{ background: 'var(--admin-bg)', borderRadius: '6px', padding: '10px 12px', border: '1px solid rgba(245,158,11,0.3)' }}>
                                  <div style={{ color: '#f59e0b', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Requested</div>
                                  <div style={{ fontWeight: '600', color: 'var(--admin-text-primary)' }}>{sub.planName}</div>
                                  <div style={{ color: 'var(--admin-text-secondary)', marginTop: '2px' }}>{intervalLabel} · {fmtPrice(sub.price, sub.billingInterval, sub.currency)}</div>
                                </div>
                              </div>
                              {/* Domain */}
                              <div style={{ fontSize: '12px', color: 'var(--admin-text-secondary)', marginBottom: '12px' }}>
                                <i className="fa-solid fa-globe" style={{ marginRight: 4 }}></i>{sub.domain || 'Domain not set'}
                              </div>
                              {/* Action buttons */}
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button className="admin-btn admin-btn--primary" onClick={() => handleApproveLegacyDowngrade(sub)} disabled={saving[sub.id]}>
                                  <i className="fa-solid fa-check"></i> Approve Downgrade
                                </button>
                                <button className="admin-btn admin-btn--ghost" onClick={() => handleCancelLegacyDowngrade(sub)} disabled={saving[sub.id]} style={{ border: '1px solid var(--admin-border)' }}>
                                  <i className="fa-solid fa-ban"></i> Cancel (No Refund)
                                </button>
                              </div>
                              {saveStatus[sub.id] && <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: saveStatus[sub.id].includes('Error') ? '#f87171' : 'var(--admin-success)' }}>{saveStatus[sub.id]}</p>}
                            </div>
                          );
                        })()}
                        {sub.isUpgradePending && (
                          <div className="admin-alert" style={{ marginBottom: '16px', color: 'var(--admin-text-secondary)', fontSize: '13px' }}>
                            <i className="fa-solid fa-hourglass-half" style={{marginRight:6}}></i>
                            {sub.upgradeType === 'billing_change' ? 'Billing cycle change' : sub.upgradeType === 'plan_downgrade' ? 'Plan downgrade' : 'Plan upgrade'} pending — approve or cancel via the <strong>active subscription's</strong> row above.
                            {sub.proratedCredit > 0 && <span style={{ marginLeft: '8px', color: 'var(--admin-success)' }}>· Credit: <strong>${(sub.proratedCredit / 100).toFixed(2)}</strong></span>}
                          </div>
                        )}
                        {sub.needsUndeploy && (
                          <div className="admin-alert admin-alert--info" style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                            <span><i className="fa-solid fa-triangle-exclamation" style={{marginRight:4}}></i> Cancelled but <strong>srv {sub.srv}</strong> still assigned. Click Undeploy to remove containers.</span>
                            <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => handleMarkUndeployed(sub.id)} disabled={markingUndeployed[sub.id]}>{markingUndeployed[sub.id] ? 'Undeploying...' : 'Undeploy'}</button>
                          </div>
                        )}

                        {/* Latest deploy status indicator */}
                        {latestDeployStatus[sub.id] && (
                          <div style={{ marginBottom: '12px', fontSize: '12px', color: 'var(--admin-text-secondary)' }}>
                            <i className="fa-solid fa-server" style={{ marginRight: 4 }}></i>
                            Last deployment action: {deployStatusBadge(sub.id)}
                          </div>
                        )}

                        {/* DNS setup preference */}
                        {sub.dnsHelp && (
                          <div style={{ marginBottom: '16px', background: 'var(--admin-bg)', border: `1px solid ${sub.dnsHelp === 'expert' ? 'rgba(251,191,36,0.4)' : 'rgba(16,185,129,0.3)'}`, borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <i className={`fa-solid ${sub.dnsHelp === 'expert' ? 'fa-headset' : 'fa-check-circle'}`} style={{ color: sub.dnsHelp === 'expert' ? '#fbbf24' : '#10b981', fontSize: '14px', flexShrink: 0 }} />
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--admin-text-muted)', marginBottom: '2px' }}>DNS Setup Preference</div>
                              {sub.dnsHelp === 'expert'
                                ? <span style={{ fontSize: '13px', color: '#fbbf24', fontWeight: '600' }}>Requested expert assistance — customer needs help with DNS configuration</span>
                                : <span style={{ fontSize: '13px', color: '#10b981', fontWeight: '600' }}>Will configure DNS themselves</span>
                              }
                            </div>
                          </div>
                        )}

                        {/* Marketing attribution — which page/section/CTA this subscription came from */}
                        {(sub.utm_source || sub.utm_medium || sub.utm_campaign || sub.utm_content || sub.landing_page) && (
                          <div style={{ marginBottom: '16px', background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: '8px', padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                              <i className="fa-solid fa-chart-pie" style={{ color: '#3b82f6', fontSize: '13px' }} />
                              <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--admin-text-muted)' }}>Marketing Attribution</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px', fontSize: '12px' }}>
                              {[
                                ['Source', sub.utm_source],
                                ['Section', sub.utm_medium],
                                ['Page', sub.utm_campaign],
                                ['CTA', sub.utm_content],
                                ['Landing', sub.landing_page],
                              ].map(([label, val]) => (
                                <div key={label}>
                                  <div style={{ color: 'var(--admin-text-muted)', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{label}</div>
                                  <div style={{ color: 'var(--admin-text-primary)', wordBreak: 'break-all' }}>{val || '—'}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Fraud prevention: device fingerprint + IP (all checkouts) */}
                        {(sub.checkout_ip || sub.checkout_fingerprint || sub.signup_ip || sub.signup_fingerprint) && (
                          <div style={{ marginBottom: '16px', background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: '8px', padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                              <i className="fa-solid fa-shield-halved" style={{ color: '#f59e0b', fontSize: '13px' }} />
                              <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--admin-text-muted)' }}>Fraud Prevention</span>
                              {sub.checkout_is_trial === false && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', background: 'rgba(99,102,241,0.15)', color: '#818cf8', fontWeight: '600' }}>PAID CHECKOUT</span>}
                              {sub.checkout_is_trial === true && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: '600' }}>TRIAL CHECKOUT</span>}
                              <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--admin-text-muted)' }}>Click IP or fingerprint to filter by that value</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                              {(sub.checkout_ip || sub.signup_ip) && (() => {
                                const ip = sub.checkout_ip || sub.signup_ip;
                                const isActive = filterFraud?.type === 'ip' && filterFraud?.value === ip;
                                const matchCount = subscriptions.filter(s => s.checkout_ip === ip || s.signup_ip === ip).length;
                                return (
                                  <div>
                                    <div style={{ color: 'var(--admin-text-muted)', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>IP Address</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                      <code style={{ color: 'var(--admin-text-primary)', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>{ip}</code>
                                      <button
                                        onClick={() => isActive ? clearFraudFilter() : applyFraudFilter('ip', ip)}
                                        style={{ background: isActive ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.06)', border: isActive ? '1px solid #f59e0b' : '1px solid var(--admin-border)', color: isActive ? '#f59e0b' : 'var(--admin-text-secondary)', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}
                                        title={isActive ? 'Clear IP filter' : `Show all subscriptions from this IP`}
                                      >
                                        {isActive ? <><i className="fa-solid fa-times" style={{ marginRight: 3 }} />Clear</> : <><i className="fa-solid fa-filter" style={{ marginRight: 3 }} />{matchCount} sub{matchCount !== 1 ? 's' : ''}</>}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}
                              {(sub.checkout_fingerprint || sub.signup_fingerprint) && (() => {
                                const fp = sub.checkout_fingerprint || sub.signup_fingerprint;
                                const isActive = filterFraud?.type === 'fingerprint' && filterFraud?.value === fp;
                                const matchCount = subscriptions.filter(s => s.checkout_fingerprint === fp || s.signup_fingerprint === fp).length;
                                return (
                                  <div>
                                    <div style={{ color: 'var(--admin-text-muted)', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Device Fingerprint</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                      <code style={{ color: 'var(--admin-text-primary)', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', wordBreak: 'break-all' }} title={fp}>
                                        {fp.slice(0, 16)}…
                                      </code>
                                      <button
                                        onClick={() => isActive ? clearFraudFilter() : applyFraudFilter('fingerprint', fp)}
                                        style={{ background: isActive ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.06)', border: isActive ? '1px solid #f59e0b' : '1px solid var(--admin-border)', color: isActive ? '#f59e0b' : 'var(--admin-text-secondary)', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}
                                        title={isActive ? 'Clear device filter' : `Show all subscriptions from this device`}
                                      >
                                        {isActive ? <><i className="fa-solid fa-times" style={{ marginRight: 3 }} />Clear</> : <><i className="fa-solid fa-filter" style={{ marginRight: 3 }} />{matchCount} sub{matchCount !== 1 ? 's' : ''}</>}
                                      </button>
                                      <button
                                        style={{ background: 'none', border: 'none', color: 'var(--admin-accent)', cursor: 'pointer', fontSize: '11px', padding: '0' }}
                                        onClick={() => navigator.clipboard.writeText(fp)}
                                        title="Copy full fingerprint"
                                      >copy</button>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                        <div className="admin-edit-form-grid">
                          <div>
                            <label className="admin-label">SERVICE STATUS</label>
                            <select className="admin-select" value={editFields[sub.id]?.service_status || 'pending'} onChange={e => handleFieldChange(sub.id, 'service_status', e.target.value)}>
                              <option value="pending">pending</option><option value="active">active</option><option value="suspended">suspended</option><option value="rejected">rejected</option><option value="undeployed">undeployed</option>
                            </select>
                          </div>
                          {/* Server dropdown for pending→active, otherwise text input */}
                          {needsServerSelect(sub, editFields[sub.id]) ? (
                            <div>
                              <label className="admin-label">SELECT SERVER <span style={{ color: '#ef4444' }}>*</span></label>
                              <select className="admin-select" value={editFields[sub.id]?.server_id || ''} onChange={e => handleFieldChange(sub.id, 'server_id', e.target.value)}>
                                <option value="">— Select a server —</option>
                                {servers.map(s => (
                                  <option key={s.id} value={s.id}>{s.name} — {s.hostname || s.ip_address} ({s.current_deployments || 0}/{s.max_deployments || '?'})</option>
                                ))}
                              </select>
                              {!editFields[sub.id]?.server_id && <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#ef4444' }}>Server selection required for deployment</p>}
                            </div>
                          ) : isPendingToActive(sub, editFields[sub.id]) && deploymentMode === 'n8n' ? (
                            <div>
                              <label className="admin-label">SELECT SERVER <span style={{ color: '#6b7280' }}>(optional)</span></label>
                              <select className="admin-select" value={editFields[sub.id]?.server_id || ''} onChange={e => handleFieldChange(sub.id, 'server_id', e.target.value)}>
                                <option value="">— No server —</option>
                                {servers.map(s => (
                                  <option key={s.id} value={s.id}>{s.name} — {s.hostname || s.ip_address} ({s.current_deployments || 0}/{s.max_deployments || '?'})</option>
                                ))}
                              </select>
                              <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>{deploymentMode === 'n8n' ? 'Will be sent to n8n workflow' : 'Metadata will be updated (manual deployment)'}</p>
                            </div>
                          ) : (
                            <div>
                              <label className="admin-label">SERVER (SRV)</label>
                              <input className="admin-input" type="text" value={editFields[sub.id]?.srv || ''} onChange={e => handleFieldChange(sub.id, 'srv', e.target.value)} placeholder="e.g. 1, 2, 3" />
                            </div>
                          )}
                          {subAccessValues[sub.id] !== undefined && (
                            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--admin-border)', paddingTop: '12px', marginTop: '4px' }}>
                              <label className="admin-label">PLAN ACCESS FIELDS</label>
                              {subAccessValues[sub.id].length > 0 ? subAccessValues[sub.id].map((av, idx) => (
                                <div key={av.field_key} className="admin-access-field-row">
                                  <span style={{ color: 'var(--admin-text-secondary)', fontSize: '12px', fontWeight: '600' }}>{av.field_label || av.field_key}</span>
                                  {av.field_type === 'textarea' ? (
                                    <textarea className="admin-input" value={av.field_value || ''} placeholder="Enter details..." rows={3} onChange={e => { const arr = [...subAccessValues[sub.id]]; arr[idx] = { ...arr[idx], field_value: e.target.value }; setSubAccessValues(prev => ({ ...prev, [sub.id]: arr })); }} style={{ resize: 'vertical' }} />
                                  ) : (
                                    <input className="admin-input" type="text" value={av.field_value || ''} placeholder={av.field_type === 'url' ? 'https://...' : 'Value...'} onChange={e => { const arr = [...subAccessValues[sub.id]]; arr[idx] = { ...arr[idx], field_value: e.target.value }; setSubAccessValues(prev => ({ ...prev, [sub.id]: arr })); }} />
                                  )}
                                  <label className="admin-checkbox"><input type="checkbox" checked={!!av.enabled} onChange={e => { const arr = [...subAccessValues[sub.id]]; arr[idx] = { ...arr[idx], enabled: e.target.checked ? 1 : 0 }; setSubAccessValues(prev => ({ ...prev, [sub.id]: arr })); }} /> Enabled</label>
                                </div>
                              )) : <p style={{ color: 'var(--admin-text-muted)', fontSize: '12px', margin: 0, fontStyle: 'italic' }}>No access fields defined for this plan.</p>}
                            </div>
                          )}
                        </div>

                        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <button className="admin-btn admin-btn--primary" onClick={() => handleSave(sub.id, false)} disabled={isSaveDisabled(sub, editFields[sub.id])}>{saving[sub.id] ? 'Saving...' : 'Save + Send Email'}</button>
                          <button className="admin-btn admin-btn--secondary" onClick={() => handleSave(sub.id, true)} disabled={isSaveDisabled(sub, editFields[sub.id])}>{saving[sub.id] ? 'Saving...' : 'Save Without Email'}</button>
                          <button className="admin-btn admin-btn--ghost" onClick={() => setExpandedId(null)}>Close</button>
                          {saveStatus[sub.id] && <span style={{ fontSize: '13px', color: saveStatus[sub.id].includes('Error') ? '#f87171' : 'var(--admin-success)' }}>{saveStatus[sub.id]}</span>}
                          {(() => {
                            const action = getDeployAction(sub, editFields[sub.id]);
                            if (action === 'deploy') {
                              const deployHint = deploymentMode === 'ssh' ? 'This will deploy containers to the selected server' : deploymentMode === 'n8n' ? 'This will send deploy action to n8n webhook' : 'This will activate the subscription (metadata only)';
                              return <span style={{ fontSize: '12px', color: '#3b82f6', fontStyle: 'italic' }}><i className="fa-solid fa-rocket" style={{marginRight:4}}></i> {deployHint}{sub.status === 'trialing' ? ' and start the trial' : ''}</span>;
                            }
                            if (action === 'suspend') {
                              const suspendHint = deploymentMode === 'ssh' ? 'This will stop containers on the server via SSH' : deploymentMode === 'n8n' ? 'This will send suspend action to n8n' : 'This will update status to suspended';
                              return <span style={{ fontSize: '12px', color: '#ef4444', fontStyle: 'italic', fontWeight: '600' }}><i className="fa-solid fa-pause" style={{marginRight:4}}></i> {suspendHint}</span>;
                            }
                            if (action === 'unsuspend') {
                              const unsuspendHint = deploymentMode === 'ssh' ? 'This will restart containers on the server via SSH' : deploymentMode === 'n8n' ? 'This will send unsuspend action to n8n' : 'This will update status to active';
                              return <span style={{ fontSize: '12px', color: '#22c55e', fontStyle: 'italic' }}><i className="fa-solid fa-play" style={{marginRight:4}}></i> {unsuspendHint}</span>;
                            }
                            if (action === 'undeploy') {
                              const undeployHint = deploymentMode === 'ssh' ? 'This will permanently remove containers from the server' : deploymentMode === 'n8n' ? 'This will send undeploy action to n8n' : 'This will mark as undeployed';
                              return <span style={{ fontSize: '12px', color: '#ef4444', fontStyle: 'italic', fontWeight: '600' }}><i className="fa-solid fa-trash" style={{marginRight:4}}></i> {undeployHint}</span>;
                            }
                            if (editFields[sub.id]?.service_status === 'active' && !action) return <span style={{ fontSize: '12px', color: 'var(--admin-text-muted)', fontStyle: 'italic' }}>Saving with "active" will send the activation email{sub.status === 'trialing' ? ' and start the 7-day trial from now' : ''}</span>;
                            if (editFields[sub.id]?.service_status === 'rejected') return <span style={{ fontSize: '12px', color: '#f87171', fontStyle: 'italic', fontWeight: '600' }}><i className="fa-solid fa-triangle-exclamation" style={{marginRight:4}}></i> This will cancel the Stripe subscription and email the customer</span>;
                            return null;
                          })()}
                        </div>

                        {/* Deployment log panel */}
                        {deployLog[sub.id]?.log && (
                          <div style={{ marginTop: '16px', border: '1px solid var(--admin-border)', borderRadius: '8px', overflow: 'hidden' }}>
                            <div style={{ padding: '8px 12px', background: 'var(--admin-bg-secondary)', borderBottom: '1px solid var(--admin-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '12px', fontWeight: '600' }}>
                                <i className={`fa-solid ${deployLog[sub.id].log.status === 'running' ? 'fa-spinner fa-spin' : deployLog[sub.id].log.status === 'success' ? 'fa-check-circle' : 'fa-times-circle'}`} style={{ marginRight: 6, color: deployLog[sub.id].log.status === 'running' ? '#3b82f6' : deployLog[sub.id].log.status === 'success' ? '#22c55e' : '#ef4444' }}></i>
                                {deployLog[sub.id].log.action} — {deployLog[sub.id].log.status}
                              </span>
                              {!deployLog[sub.id].polling && (
                                <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => setDeployLog(prev => { const next = { ...prev }; delete next[sub.id]; return next; })} style={{ fontSize: '11px' }}>Dismiss</button>
                              )}
                            </div>
                            <pre style={{ margin: 0, padding: '12px', fontSize: '11px', maxHeight: '300px', overflow: 'auto', background: '#0d1117', color: '#c9d1d9', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                              {deployLog[sub.id].log.output || 'Waiting for output...'}
                            </pre>
                          </div>
                        )}

                        <div style={{ marginTop: '12px' }}><code style={{ fontSize: '11px', color: 'var(--admin-text-muted)' }}>{sub.id}</code></div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {searched.length === 0 && <p style={{ textAlign: 'center', color: 'var(--admin-text-muted)', padding: '40px 0' }}>No subscriptions found.</p>}
        </div>
      </div>

      {/* Pagination footer */}
      {pageSize !== 0 && totalPages > 1 && (
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: 'var(--admin-text-muted)' }}>
            Showing {((safePage - 1) * pageSize) + 1}&ndash;{Math.min(safePage * pageSize, searched.length)} of {searched.length}{search || filterStatus !== 'all' ? ' (filtered)' : ''}
          </span>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setPage(1)} disabled={safePage === 1}><i className="fa-solid fa-angles-left" /></button>
            <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}><i className="fa-solid fa-angle-left" /></button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 7) p = i + 1;
              else if (safePage <= 4) p = i + 1;
              else if (safePage >= totalPages - 3) p = totalPages - 6 + i;
              else p = safePage - 3 + i;
              return (
                <button key={p} className={`admin-btn admin-btn--sm ${safePage === p ? 'admin-btn--primary' : 'admin-btn--ghost'}`} onClick={() => setPage(p)}>{p}</button>
              );
            })}
            <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}><i className="fa-solid fa-angle-right" /></button>
            <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}><i className="fa-solid fa-angles-right" /></button>
          </div>
          <span style={{ fontSize: '12px', color: 'var(--admin-text-muted)' }}>Page {safePage} of {totalPages}</span>
        </div>
      )}
      {pageSize === 0 && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--admin-text-muted)' }}>
          Showing all {searched.length} subscription{searched.length !== 1 ? 's' : ''}{search || filterStatus !== 'all' ? ' (filtered)' : ''}
        </div>
      )}

      {/* Send Email Modal */}
      {emailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if (e.target === e.currentTarget) setEmailModal(null); }}>
          <div className="admin-card" style={{ width: '520px', maxWidth: '100%', maxHeight: '88vh', overflowY: 'auto', margin: 0 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
              <h3 className="admin-card-title" style={{ margin: 0 }}>Send Email</h3>
              <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setEmailModal(null)} style={{ fontSize: '16px', lineHeight: 1 }}>✕</button>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--admin-text-secondary)', marginBottom: '16px' }}>
              <i className="fa-solid fa-user" style={{ marginRight: 4 }}></i><strong>{emailModal.sub.customerEmail}</strong>
              <span style={{ margin: '0 8px', color: 'var(--admin-border)' }}>·</span>
              {emailModal.sub.planName}{emailModal.sub.domain ? ` · ${emailModal.sub.domain}` : ''}
            </p>

            {/* Template grid */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.7px', color: 'var(--admin-text-muted)', marginBottom: '8px' }}>Select Template</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                {EMAIL_TEMPLATES.map(t => (
                  <button key={t.id}
                    onClick={() => { setEmailTemplate(t.id); setEmailStatus(''); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
                      borderRadius: '6px', border: `1px solid ${emailTemplate === t.id ? 'var(--admin-accent)' : 'var(--admin-border)'}`,
                      background: emailTemplate === t.id ? 'rgba(26,173,159,0.1)' : 'var(--admin-elevated)',
                      color: emailTemplate === t.id ? 'var(--admin-accent)' : 'var(--admin-text-primary)',
                      cursor: 'pointer', textAlign: 'left', fontSize: '12px', fontWeight: emailTemplate === t.id ? '600' : '400',
                      transition: 'all 0.15s',
                    }}>
                    <i className={`fa-solid ${t.icon}`} style={{ width: 14, flexShrink: 0 }}></i>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Template description + extra fields */}
            {(() => {
              const tpl = EMAIL_TEMPLATES.find(t => t.id === emailTemplate);
              if (!tpl) return null;
              return (
                <>
                  <p style={{ fontSize: '12px', color: 'var(--admin-text-secondary)', marginBottom: '14px', fontStyle: 'italic', borderLeft: '3px solid var(--admin-border)', paddingLeft: '8px' }}>
                    {tpl.desc}
                  </p>
                  {tpl.fields.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.7px', color: 'var(--admin-text-muted)', marginBottom: '8px' }}>Additional Fields</div>
                      {tpl.fields.map(field => (
                        <div key={field.key} style={{ marginBottom: '10px' }}>
                          <label className="admin-label" style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
                            {field.label}{field.optional && <span style={{ color: 'var(--admin-text-muted)', fontWeight: '400' }}> (optional)</span>}
                          </label>
                          {field.type === 'textarea' ? (
                            <textarea className="admin-input" rows={3}
                              value={emailOverrides[field.key] || ''}
                              onChange={e => setEmailOverrides(prev => ({ ...prev, [field.key]: e.target.value }))}
                              style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
                          ) : (
                            <input type="text" className="admin-input"
                              value={emailOverrides[field.key] || ''}
                              onChange={e => setEmailOverrides(prev => ({ ...prev, [field.key]: e.target.value }))}
                              style={{ width: '100%', boxSizing: 'border-box' }} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Status messages */}
            {emailStatus.startsWith('sent:') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: 'rgba(26,173,159,0.1)', border: '1px solid var(--admin-success)', borderRadius: '6px', marginBottom: '14px', fontSize: '13px', color: 'var(--admin-success)' }}>
                <i className="fa-solid fa-circle-check"></i>
                Email sent successfully to <strong>{emailStatus.slice(5)}</strong>
              </div>
            )}
            {emailStatus.startsWith('error:') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: 'rgba(248,113,113,0.1)', border: '1px solid #f87171', borderRadius: '6px', marginBottom: '14px', fontSize: '13px', color: '#f87171' }}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                {emailStatus.slice(6)}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid var(--admin-border)', paddingTop: '14px', marginTop: '4px' }}>
              <button className="admin-btn admin-btn--ghost" onClick={() => setEmailModal(null)}>Close</button>
              <button className="admin-btn admin-btn--primary" onClick={handleSendEmail} disabled={emailSending}>
                <i className={`fa-solid ${emailSending ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} style={{ marginRight: 6 }}></i>
                {emailSending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default AdminSubscriptions;
